import React, { useState, useEffect, useRef } from 'react';
import { RotateCcw, Download, PanelLeftClose, PanelLeftOpen, BookOpen, Target, Clock, AlertCircle, Play, FileText, Settings, ChevronDown, Search, Brain, Zap } from 'lucide-react';
import DeepSeekProgressModal from './DeepSeekProgressModal';
import deepseekFigureParser from '../services/deepseekFigureParser';
import hkdseQuestionLoader from '../services/hkdseQuestionLoader';
import knowledgeGraphBridge from '../services/knowledgeGraphAssessmentBridge';
import geometryTextAnalyzer from '../services/geometryTextAnalyzer';
import { renderMathJax, ensureMathJaxReady } from '../utils/mathJaxUtils';

const Assessment = () => {
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [questionInput, setQuestionInput] = useState('');
  const [processedContent, setProcessedContent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFigure, setSelectedFigure] = useState('triangle');
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPoint, setDraggedPoint] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  // New states for dropdown and DeepSeek integration
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressData, setProgressData] = useState({
    stage: 'connecting',
    message: '正在連接 DeepSeek API...',
    tokensUsed: 0,
    timeElapsed: 0,
    cost: 0
  });
  
  // Knowledge Graph integration states
  const [useKnowledgeGraph, setUseKnowledgeGraph] = useState(true);
  const [knowledgeGraphQuestions, setKnowledgeGraphQuestions] = useState([]);
  const [selectedKnowledgeGraphQuestion, setSelectedKnowledgeGraphQuestion] = useState(null);
  const [isKnowledgeGraphDropdownOpen, setIsKnowledgeGraphDropdownOpen] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  // Load questions on component mount
  useEffect(() => {
    loadQuestions();
    loadKnowledgeGraphQuestions();
  }, []);

  // Reload Knowledge Graph questions when year changes
  useEffect(() => {
    if (selectedYear) {
      loadKnowledgeGraphQuestions();
    }
  }, [selectedYear]);

  const loadQuestions = async () => {
    setIsLoadingQuestions(true);
    try {
      const loadedQuestions = await hkdseQuestionLoader.loadAllQuestions();
      setQuestions(loadedQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const loadKnowledgeGraphQuestions = () => {
    try {
      // Load available years first
      const years = knowledgeGraphBridge.getAvailableHKDSEYears();
      setAvailableYears(years);
      
      // Set default year to the most recent
      if (years.length > 0 && !selectedYear) {
        setSelectedYear(years[0]);
      }
      
      // Load questions for the selected year
      if (selectedYear) {
        const yearQuestions = knowledgeGraphBridge.getHKDSEProblemsByYear(selectedYear);
        console.log('Loaded questions for year', selectedYear, ':', yearQuestions.length);
        console.log('Sample question:', yearQuestions[0]);
        
        // Check for the specific perpendicular lines question
        const perpendicularQuestion = yearQuestions.find(q => 
          q.problem && q.problem.toLowerCase().includes('perpendicular') && 
          q.problem.toLowerCase().includes('l1') && q.problem.toLowerCase().includes('l2')
        );
        if (perpendicularQuestion) {
          console.log('Found perpendicular lines question:', perpendicularQuestion);
        } else {
          console.log('Perpendicular lines question not found');
        }
        
        setKnowledgeGraphQuestions(yearQuestions);
      }
    } catch (error) {
      console.error('Error loading Knowledge Graph questions:', error);
    }
  };

  // Filter questions based on search term
  const filteredQuestions = questions.filter(q => 
    q.displayText.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.problem.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter Knowledge Graph questions based on search term
  const filteredKnowledgeGraphQuestions = knowledgeGraphQuestions.filter(q => 
    q.displayText.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.problem.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle question selection from dropdown
  const handleQuestionSelect = (question) => {
    setSelectedQuestion(question);
    setIsDropdownOpen(false);
    setSearchTerm('');
    
    // Load question content into input
    const questionContent = hkdseQuestionLoader.getQuestionContent(question);
    setQuestionInput(questionContent);
  };

  // Handle Knowledge Graph question selection
  const handleKnowledgeGraphQuestionSelect = async (question) => {
    setSelectedKnowledgeGraphQuestion(question);
    setIsKnowledgeGraphDropdownOpen(false);
    setSearchTerm('');
    
    // Debug: Log the question and its text
    console.log('Selected question:', question);
    console.log('Question text:', question.problem);
    
    // Load question content into input using Knowledge Graph bridge
    const questionContent = knowledgeGraphBridge.getQuestionContent(question);
    setQuestionInput(questionContent);
    
    // Process the question immediately using structured data
    const parsedContent = knowledgeGraphBridge.getLatexContent(question);
    setProcessedContent(parsedContent);
    
    // Trigger MathJax rendering after a short delay
    setTimeout(async () => {
      try {
        await ensureMathJaxReady();
        await renderMathJax(200);
      } catch (error) {
        console.log('MathJax rendering error:', error);
      }
    }, 300);
    
    // Directly process the question text for SVG viewer
    const questionText = question.problem || '';
    
    // Check if there's a meaningful question text
    const hasQuestionText = questionText && 
      (questionText.toLowerCase().includes('figure') || 
       questionText.toLowerCase().includes('triangle') ||
       questionText.toLowerCase().includes('circle') ||
       questionText.toLowerCase().includes('square') ||
       questionText.toLowerCase().includes('points') ||
       questionText.toLowerCase().includes('lines') ||
       questionText.toLowerCase().includes('perpendicular') ||
       questionText.toLowerCase().includes('intersect') ||
       questionText.toLowerCase().includes('graph') ||
       questionText.toLowerCase().includes('coordinate') ||
       questionText.toLowerCase().includes('axis'));
    
    if (hasQuestionText) {
      // Process the question text for SVG viewer
      setIsProcessing(true);
      setShowProgressModal(true);
      setProgressData({
        stage: 'connecting',
        message: '正在連接 DeepSeek API...',
        tokensUsed: 0,
        timeElapsed: 0,
        cost: 0
      });
      
      try {
        const result = await deepseekFigureParser.parseFigureDescription(
          questionText,  // Use the full question text instead of figure description
          questionText,  // Pass the same question text as context
          (progress) => {
            setProgressData(progress);
          }
        );
        
        if (result.success) {
          // Update figure data with DeepSeek analysis
          const figureType = result.svgInstructions.figureType || 'triangle';
          setSelectedFigure(figureType);
          
          // Update figure data
          setFigureData(prev => ({
            ...prev,
            [figureType]: result.svgInstructions
          }));
          
          // Update progress to completed
          setProgressData(prev => ({
            ...prev,
            stage: 'completed',
            message: '圖形解析完成！',
            cost: result.cost
          }));
        } else {
          // Fallback to default parsing
          const figureType = generateFigureFromQuestion(parsedContent);
          setSelectedFigure(figureType);
          
          const customFigureData = createCustomFigureData(parsedContent);
          setFigureData(prev => ({
            ...prev,
            [figureType]: customFigureData
          }));
          
          setProgressData(prev => ({
            ...prev,
            stage: 'error',
            message: '圖形解析失敗，使用預設圖形',
            error: result.error
          }));
        }
      } catch (error) {
        console.error('Error processing question:', error);
        setProgressData(prev => ({
          ...prev,
          stage: 'error',
          message: '處理失敗',
          error: error.message
        }));
      } finally {
        setIsProcessing(false);
      }
    } else {
      // No meaningful question text - clear the SVG viewer
      console.log('No meaningful question text found, clearing SVG viewer');
      
      // Clear the figure data to show empty SVG viewer
      setFigureData(prev => ({
        ...prev,
        triangle: { points: {}, lines: [], circles: [] },
        circle: { points: {}, lines: [], circles: [] },
        rectangle: { points: {}, lines: [], circles: [] }
      }));
    }
  };

  // Handle year selection
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setIsYearDropdownOpen(false);
    setSelectedKnowledgeGraphQuestion(null);
    setSearchTerm('');
  };

  // Function to parse question input and generate structured content
  const parseQuestionInput = (input) => {
    // This is a simplified parser - in a real application, you might use more sophisticated parsing
    const lines = input.split('\n').filter(line => line.trim());
    
    // Extract question number and marks
    const firstLine = lines[0];
    const questionMatch = firstLine.match(/Question\s+(\d+)\s*\(([^)]+)\)/i);
    
    let questionNumber = "Question 1";
    let marksInfo = "(2 marks for part a, 5 marks for part b)";
    
    if (questionMatch) {
      questionNumber = `Question ${questionMatch[1]}`;
      marksInfo = `(${questionMatch[2]})`;
    }

    // Extract problem statement
    const problemLine = lines.find(line => 
      line.includes('triangle') || 
      line.includes('Figure') || 
      line.includes('ABC') ||
      line.includes('points') ||
      line.includes('circle') ||
      line.includes('square') ||
      line.includes('hexagon')
    ) || lines[1] || "In Figure 1, $ABC$ is a triangle with points $D$, $E$ and $M$ on side $BC$.";

    // Extract figure description
    const figureDescLine = lines.find(line => 
      line.toLowerCase().includes('figure') && 
      line.toLowerCase().includes('description')
    ) || "Triangle ABC with points D, E, M on side BC. Lines AD, AE, and AM are drawn from vertex A to these points on the base.";

    // Extract parts
    const parts = [];
    let currentPart = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for part headers like "(a)", "(b)", etc.
      const partMatch = line.match(/^\(([a-z])\)/i);
      if (partMatch) {
        if (currentPart) {
          parts.push(currentPart);
        }
        
        currentPart = {
          id: partMatch[1].toLowerCase(),
          question: line.replace(/^\([a-z]\)\s*/i, ''),
          marks: 2, // Default marks
          subParts: []
        };
        
        // Try to extract marks from the line
        const marksMatch = line.match(/(\d+)\s*marks?/i);
        if (marksMatch) {
          currentPart.marks = parseInt(marksMatch[1]);
        }
      }
      // Check for sub-parts like "(i)", "(ii)", etc.
      else if (line.match(/^\([ivx]+\)/i)) {
        if (currentPart) {
          const subPartMatch = line.match(/^\(([ivx]+)\)/i);
          const subPart = {
            id: subPartMatch[1].toLowerCase(),
            question: line.replace(/^\([ivx]+\)\s*/i, ''),
            marks: 2 // Default marks
          };
          
          // Try to extract marks from the line
          const subMarksMatch = line.match(/(\d+)\s*marks?/i);
          if (subMarksMatch) {
            subPart.marks = parseInt(subMarksMatch[1]);
          }
          
          currentPart.subParts.push(subPart);
        }
      }
      // Add line to current part if it's not empty
      else if (line && currentPart) {
        if (!currentPart.question.includes(line)) {
          currentPart.question += ' ' + line;
        }
      }
    }
    
    // Add the last part
    if (currentPart) {
      parts.push(currentPart);
    }

    // If no parts were found, create a default part
    if (parts.length === 0) {
      parts.push({
        id: 'a',
        question: "Prove that $\\triangle ACD \\cong \\triangle ABE$.",
        marks: 2
      });
    }

    return {
      question: `${questionNumber} ${marksInfo}`,
      problem: problemLine,
      figureDescription: figureDescLine,
      parts: parts
    };
  };

  // Function to generate figure data based on question content
  const generateFigureFromQuestion = (questionContent) => {
    const { problem, figureDescription } = questionContent;
    const text = (problem + ' ' + figureDescription).toLowerCase();
    
    // Detect figure type from question content
    if (text.includes('circle') || 
        text.includes('circumference') || 
        text.includes('abcd is a circle') ||
        text.includes('chord') ||
        (text.includes('point') && text.includes('line') && text.includes('circumference'))) {
      return 'circle';
    } else if (text.includes('square') || text.includes('quadrilateral')) {
      return 'square';
    } else if (text.includes('hexagon')) {
      return 'hexagon';
    } else {
      return 'triangle'; // Default
    }
  };

  // Function to create custom figure data based on question
  const createCustomFigureData = (questionContent) => {
    // Use the better extraction function
    const { points: extractedPoints, lines: extractedLines, extensions: extractedExtensions } = extractGeometryFromQuestion(questionContent);
    
    // Create basic figure based on detected type
    const figureType = generateFigureFromQuestion(questionContent);
    
    if (figureType === 'circle') {
      // Create circle with points mentioned in question
      const centerPoint = 'O';
      const radius = 120;
      const pointPositions = {};
      
      // Always add center point for circle figures
      pointPositions[centerPoint] = {
        x: 200,
        y: 200,
        label: centerPoint
      };
      
      // Position points around circle based on question content
      const pointArray = extractedPoints.filter(p => p !== centerPoint);
      
      // For circle questions, position points around circumference
      // Use specific angles for better positioning based on common patterns
      const angleMap = {
        'A': 0,        // Right side (0 degrees)
        'B': Math.PI/2, // Top (90 degrees)
        'C': Math.PI,   // Left side (180 degrees)
        'D': 3*Math.PI/2, // Bottom (270 degrees)
        'E': Math.PI/4, // Top-right (45 degrees)
        'F': 3*Math.PI/4, // Top-left (135 degrees)
        'G': 5*Math.PI/4, // Bottom-left (225 degrees)
        'H': 7*Math.PI/4  // Bottom-right (315 degrees)
      };
      
      pointArray.forEach((point, index) => {
        // Use predefined angle if available, otherwise distribute evenly
        const angle = angleMap[point] !== undefined ? 
          angleMap[point] : (index * 2 * Math.PI) / pointArray.length;
        
        pointPositions[point] = {
          x: 200 + radius * Math.cos(angle),
          y: 200 + radius * Math.sin(angle),
          label: point
        };
      });
      
      // Use extracted lines or create default connections
      const lines = extractedLines.length > 0 ? extractedLines : [];
      
      // If no specific lines found, create some default connections based on common circle patterns
      if (lines.length === 0 && pointArray.length > 1) {
        // Special case for HKDSE 2019 Question 13: Circle ABCDE with diameter AC
        if (pointArray.includes('A') && pointArray.includes('B') && 
            pointArray.includes('C') && pointArray.includes('D') && 
            pointArray.includes('E')) {
          // Position E properly on the circumference (not on the diameter)
          const centerX = 200;
          const centerY = 200;
          const radius = 120;
          
          // Reposition E to be on the circumference, not on the diameter
          pointPositions['E'] = {
            x: centerX + radius * Math.cos(Math.PI/4), // 45 degrees
            y: centerY + radius * Math.sin(Math.PI/4),
            label: 'E'
          };
          
          lines.push(
            { from: 'A', to: 'C', color: 'red', width: 1.33 },    // Diameter AC
            { from: 'B', to: 'D', color: 'green', width: 1.33 },  // Chord BD
            { from: 'O', to: 'C', color: 'blue', width: 1 },   // Radius OC
            { from: 'B', to: 'D', color: 'purple', width: 1 }  // Chord BD
          );
          
          // Add point F (intersection of BD and OC) - position it inside the circle
          pointPositions['F'] = {
            x: centerX - 30,  // Inside the circle, on OC line
            y: centerY + 60,  // Above center, on BD line
            label: 'F'
          };
          
          lines.push(
            { from: 'O', to: 'F', color: 'orange', width: 0.67 },  // Part of OC
            { from: 'F', to: 'C', color: 'orange', width: 0.67 },  // Part of OC
            { from: 'B', to: 'F', color: 'brown', width: 0.67 },   // Part of BD
            { from: 'F', to: 'D', color: 'brown', width: 0.67 }    // Part of BD
          );
        }
        // For circle with points A, B, C, D, create a quadrilateral
        else if (pointArray.includes('A') && pointArray.includes('B') && 
            pointArray.includes('C') && pointArray.includes('D')) {
          lines.push(
            { from: 'A', to: 'B', color: 'blue', width: 1 },
            { from: 'B', to: 'C', color: 'blue', width: 1 },
            { from: 'C', to: 'D', color: 'blue', width: 1 },
            { from: 'D', to: 'A', color: 'blue', width: 1 },
            { from: 'A', to: 'C', color: 'red', width: 1.33 },  // Chord AC
            { from: 'B', to: 'D', color: 'green', width: 1.33 }  // Chord BD
          );
          
          // If point E is mentioned and it's on chord AC, position it on the chord
          if (pointArray.includes('E')) {
            const pointA = pointPositions['A'];
            const pointC = pointPositions['C'];
            if (pointA && pointC) {
              pointPositions['E'] = {
                x: (pointA.x + pointC.x) / 2,
                y: (pointA.y + pointC.y) / 2,
                label: 'E'
              };
              lines.push(
                { from: 'B', to: 'E', color: 'purple', width: 1 },
                { from: 'D', to: 'E', color: 'orange', width: 1 }
              );
            }
          }
        } else {
          // Create a polygon or some basic connections
          for (let i = 0; i < pointArray.length; i++) {
            const from = pointArray[i];
            const to = pointArray[(i + 1) % pointArray.length];
            if (pointPositions[from] && pointPositions[to]) {
              lines.push({
                from,
                to,
                color: 'gray',
                width: 0.67
              });
            }
          }
        }
      }
      
      return {
        points: pointPositions,
        lines,
        circles: [{
          center: centerPoint,
          radius,
          color: 'blue',
          width: 1
        }],
        extensions: extractedExtensions || []
      };
    } else if (figureType === 'triangle') {
      // Special handling for the specific question about △ACE and △DBE
      const { problem } = questionContent;
      const problemText = (problem || '').toLowerCase();
      
      // Check if this is the specific question about extensions intersecting at E
      if (problemText.includes('extension') && problemText.includes('intersect') && 
          problemText.includes('ace') && problemText.includes('dbe')) {
        
        // Create the specific geometry for this question
        const pointPositions = {
          A: { x: 100, y: 150, label: 'A' },
          B: { x: 150, y: 150, label: 'B' },
          C: { x: 100, y: 250, label: 'C' },
          D: { x: 150, y: 250, label: 'D' },
          E: { x: 300, y: 200, label: 'E' }  // Intersection point
        };
        
        // Create the lines for this specific geometry
        const lines = [
          // Main triangle △ACE
          { from: 'A', to: 'C', color: 'blue', width: 1 },    // Base of △ACE
          { from: 'A', to: 'E', color: 'blue', width: 1 },    // Side of △ACE
          { from: 'C', to: 'E', color: 'blue', width: 1 },    // Side of △ACE
          
          // Inner triangle △DBE
          { from: 'D', to: 'B', color: 'red', width: 1 },     // Base of △DBE
          { from: 'D', to: 'E', color: 'red', width: 1 },     // Side of △DBE
          { from: 'B', to: 'E', color: 'red', width: 1 },     // Side of △DBE
          
          // Extension lines (dashed)
          { from: 'A', to: 'B', color: 'gray', width: 0.67, strokeDasharray: '5,5' },  // Extension of AB
          { from: 'C', to: 'D', color: 'gray', width: 0.67, strokeDasharray: '5,5' },  // Extension of CD
          
          // Highlight the shared side BD
          { from: 'B', to: 'D', color: 'green', width: 1.33 }
        ];
        
        return {
          points: pointPositions,
          lines,
          extensions: []
        };
      }
      
      // Enhanced triangle handling for other cases with better text interpretation
      const pointArray = extractedPoints;
      const pointPositions = {};
      
      // Position triangle points
      if (pointArray.length >= 3) {
        pointPositions[pointArray[0]] = { x: 200, y: 50, label: pointArray[0] };
        pointPositions[pointArray[1]] = { x: 100, y: 200, label: pointArray[1] };
        pointPositions[pointArray[2]] = { x: 300, y: 200, label: pointArray[2] };
        
        // Add additional points on the base if mentioned
        if (pointArray.length > 3) {
          pointPositions[pointArray[3]] = { x: 150, y: 200, label: pointArray[3] };
        }
        if (pointArray.length > 4) {
          pointPositions[pointArray[4]] = { x: 250, y: 200, label: pointArray[4] };
        }
        
        // Create lines connecting the triangle
        const lines = [
          { from: pointArray[0], to: pointArray[1], color: 'blue', width: 1 },
          { from: pointArray[1], to: pointArray[2], color: 'blue', width: 1 },
          { from: pointArray[2], to: pointArray[0], color: 'blue', width: 1 }
        ];
        
        // Add additional lines for extra points
        if (pointArray.length > 3) {
          lines.push({ from: pointArray[0], to: pointArray[3], color: 'red', width: 1 });
        }
        if (pointArray.length > 4) {
          lines.push({ from: pointArray[0], to: pointArray[4], color: 'green', width: 1 });
        }
        
        // Check for extensions in the question text
        const extensions = [];
        if (problemText.includes('extension') || problemText.includes('extended')) {
          // Add extension lines as dashed
          if (pointArray.length >= 4) {
            extensions.push({
              from: pointArray[1],
              to: pointArray[3],
              color: 'gray',
              width: 0.67,
              strokeDasharray: '5,5'
            });
          }
          if (pointArray.length >= 5) {
            extensions.push({
              from: pointArray[2],
              to: pointArray[4],
              color: 'gray',
              width: 0.67,
              strokeDasharray: '5,5'
            });
          }
        }
        
        return {
          points: pointPositions,
          lines,
          extensions: extractedExtensions || []
        };
      }
      
      // Use extracted lines or create default triangle lines
      const lines = extractedLines.length > 0 ? extractedLines : [];
      
      if (lines.length === 0 && pointArray.length >= 3) {
        lines.push(
          { from: pointArray[0], to: pointArray[1], color: 'gray', width: 1 },
          { from: pointArray[1], to: pointArray[2], color: 'gray', width: 1 },
          { from: pointArray[2], to: pointArray[0], color: 'gray', width: 1 }
        );
        
        // Add lines from first point to base points
        if (pointArray.length > 3) {
          lines.push({ from: pointArray[0], to: pointArray[3], color: 'red', width: 1.33 });
        }
        if (pointArray.length > 4) {
          lines.push({ from: pointArray[0], to: pointArray[4], color: 'green', width: 1.33 });
        }
        if (pointArray.length > 5) {
          lines.push({ from: pointArray[0], to: pointArray[5], color: 'blue', width: 1.33 });
        }
      }
      
      return {
        points: pointPositions,
        lines,
        extensions: extractedExtensions || []
      };
    } else {
      // Use default figure data for other types
      return figureData[figureType];
    }
  };

  // Function to extract specific points and lines from question text
  const extractGeometryFromQuestion = (questionContent) => {
    const { problem, figureDescription } = questionContent;
    const text = (problem + ' ' + figureDescription).toLowerCase();
    
    // Use the enhanced geometry text analyzer
    const analysis = geometryTextAnalyzer.analyzeText(text);
    
    console.log('Enhanced geometry analysis:', analysis);
    
    // Convert analysis to the expected format
    const points = Array.from(analysis.points);
    const lines = analysis.lines;
    const extensions = analysis.extensions;
    
    // Add any additional points found through pattern matching
    const additionalPoints = new Set(points);
    
    // Enhanced point pattern recognition for additional points
    const pointPatterns = [
      /points?\s+([A-Z](?:\s*,\s*[A-Z])*)/gi,
      /([A-Z])\s*,\s*([A-Z])\s*,\s*([A-Z])\s*,\s*([A-Z])/gi,
      /([A-Z])\s*,\s*([A-Z])\s*,\s*([A-Z])/gi,
      /([A-Z])\s*,\s*([A-Z])/gi,
      /([A-Z][A-Z][A-Z][A-Z])\s+is\s+a\s+circle/gi,  // "ABCD is a circle"
      /([A-Z][A-Z][A-Z])\s+is\s+a\s+triangle/gi,     // "ABC is a triangle"
      /points?\s+([A-Z])\s*,\s*([A-Z])\s*,\s*([A-Z])\s*,\s*([A-Z])/gi,  // "points A, B, C, D"
      /([A-Z])\s+is\s+located\s+on/gi,  // "Point E is located on"
      /△([A-Z][A-Z][A-Z])/gi,  // "△ABC"
      /([A-Z])\s+and\s+([A-Z])\s+intersect/gi,  // "AB and CD intersect"
      /([A-Z])\s*\(([^)]+)\)/gi,  // "A(2,6)" or "A(2, 6)"
      /point\s+([A-Z])\s*\(([^)]+)\)/gi,  // "point A(2,6)"
      /([A-Z])\s+intersects\s+([A-Z])/gi,  // "A intersects B"
      /([A-Z])\s+meets\s+([A-Z])/gi,  // "A meets B"
      /([A-Z])\s+at\s+([A-Z])/gi,  // "A at B"
    ];
    
    pointPatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const letters = match.match(/[A-Z]/g);
          if (letters) {
            letters.forEach(letter => additionalPoints.add(letter));
          }
        });
      }
    });
    
    // Also extract individual capital letters
    const individualPoints = text.match(/[A-Z]/g) || [];
    individualPoints.forEach(point => additionalPoints.add(point));
    
    // Enhanced line pattern recognition for additional lines
    const linePatterns = [
      /lines?\s+([A-Z][A-Z](?:\s*,\s*[A-Z][A-Z])*)/gi,
      /([A-Z][A-Z])/gi,
      /([A-Z][A-Z])\s*,\s*([A-Z][A-Z])/gi,  // "AB, AD, BC, BD"
      /([A-Z][A-Z])\s+parallel\s+([A-Z][A-Z])/gi,  // "AB parallel CD"
      /([A-Z][A-Z])\s+perpendicular\s+([A-Z][A-Z])/gi,  // "AB perpendicular CD"
      /([A-Z][A-Z])\s*∥\s*([A-Z][A-Z])/gi,  // "AB ∥ CD"
      /([A-Z][A-Z])\s*⊥\s*([A-Z][A-Z])/gi,  // "AB ⊥ CD"
      /([A-Z][A-Z])\s+intersects\s+([A-Z][A-Z])/gi,  // "AB intersects CD"
      /([A-Z][A-Z])\s+meets\s+([A-Z][A-Z])/gi,  // "AB meets CD"
      /([A-Z][A-Z])\s+and\s+([A-Z][A-Z])/gi,  // "AB and CD"
      /([A-Z][A-Z])\s+with\s+([A-Z][A-Z])/gi,  // "AB with CD"
      /([A-Z][A-Z])\s+through\s+([A-Z])/gi,  // "AB through C"
      /([A-Z][A-Z])\s+from\s+([A-Z])\s+to\s+([A-Z])/gi,  // "AB from A to B"
    ];
    
    const additionalLines = [];
    linePatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const lineMatches = match.match(/[A-Z][A-Z]/g);
          if (lineMatches) {
            lineMatches.forEach(line => {
              const from = line[0];
              const to = line[1];
              if (additionalPoints.has(from) && additionalPoints.has(to)) {
                // Determine line color and width based on context
                let color = 'blue';
                let width = 1;
                
                // Check for special line types
                if (text.includes('diameter') && (line.includes('A') || line.includes('C'))) {
                  color = 'red';
                  width = 1.33;
                } else if (text.includes('chord')) {
                  color = 'green';
                  width = 1;
                } else if (text.includes('radius')) {
                  color = 'orange';
                  width = 1;
                } else if (text.includes('perpendicular') || text.includes('⊥')) {
                  color = 'purple';
                  width = 1.33;
                } else if (text.includes('parallel') || text.includes('∥')) {
                  color = 'brown';
                  width = 1;
                }
                
                additionalLines.push({ from, to, color, width });
              }
            });
          }
        });
      }
    });
    
    // Enhanced extension pattern recognition for additional extensions
    const extensionPatterns = [
      /extension\s+of\s+([A-Z][A-Z])/gi,
      /([A-Z][A-Z])\s+extended/gi,
      /([A-Z][A-Z])\s+and\s+([A-Z][A-Z])\s+extensions/gi,
      /([A-Z][A-Z])\s+produced/gi,
      /([A-Z][A-Z])\s+extended\s+to\s+([A-Z])/gi,
      /([A-Z][A-Z])\s+meets\s+([A-Z][A-Z])\s+extension/gi,
      /([A-Z][A-Z])\s+intersects\s+([A-Z][A-Z])\s+extension/gi,
    ];
    
    const additionalExtensions = [];
    extensionPatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const extensionMatches = match.match(/[A-Z][A-Z]/g);
          if (extensionMatches) {
            extensionMatches.forEach(line => {
              const from = line[0];
              const to = line[1];
              if (additionalPoints.has(from) && additionalPoints.has(to)) {
                additionalExtensions.push({
                  from,
                  to,
                  color: 'gray',
                  width: 0.67,
                  strokeDasharray: '5,5'
                });
              }
            });
          }
        });
      }
    });
    
    // Enhanced intersection point recognition
    const intersectionPatterns = [
      /intersect\s+at\s+([A-Z])/gi,
      /([A-Z])\s+is\s+the\s+intersection/gi,
      /([A-Z])\s+intersects\s+([A-Z])/gi,
      /([A-Z])\s+meets\s+([A-Z])/gi,
      /([A-Z])\s+at\s+([A-Z])/gi,
      /([A-Z])\s+and\s+([A-Z])\s+intersect/gi,
      /([A-Z])\s+and\s+([A-Z])\s+meet/gi,
    ];
    
    intersectionPatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const intersectionPoint = match.match(/[A-Z]/);
          if (intersectionPoint) {
            additionalPoints.add(intersectionPoint[0]);
          }
        });
      }
    });
    
    // Enhanced coordinate point recognition
    const coordinatePatterns = [
      /point\s+([A-Z])\s*\(([^)]+)\)/gi,
      /([A-Z])\s*\(([^)]+)\)/gi,
      /([A-Z])\s+at\s*\(([^)]+)\)/gi,
      /([A-Z])\s+coordinates\s*\(([^)]+)\)/gi,
    ];
    
    coordinatePatterns.forEach(pattern => {
      const matches = text.match(pattern);
      if (matches) {
        matches.forEach(match => {
          const pointMatch = match.match(/[A-Z]/);
          if (pointMatch) {
            additionalPoints.add(pointMatch[0]);
          }
        });
      }
    });
    
    // Special handling for specific geometric constructions
    if (text.includes('quadrilateral') && text.includes('extension')) {
      // Add common points for quadrilateral with extensions
      ['A', 'B', 'C', 'D', 'E'].forEach(point => additionalPoints.add(point));
      
      // Add extension lines
      if (additionalPoints.has('A') && additionalPoints.has('B') && additionalPoints.has('E')) {
        additionalExtensions.push({
          from: 'A',
          to: 'E',
          color: 'gray',
          width: 0.67,
          strokeDasharray: '5,5'
        });
      }
      if (additionalPoints.has('C') && additionalPoints.has('D') && additionalPoints.has('E')) {
        additionalExtensions.push({
          from: 'C',
          to: 'E',
          color: 'gray',
          width: 0.67,
          strokeDasharray: '5,5'
        });
      }
    }
    
    // Special handling for perpendicular lines with coordinates
    if (text.includes('perpendicular') && text.includes('l₁') && text.includes('l₂')) {
      // Add intersection point
      additionalPoints.add('E');
      
      // Add perpendicular lines
      if (additionalPoints.has('A') && additionalPoints.has('E')) {
        additionalLines.push({ from: 'A', to: 'E', color: 'blue', width: 1 });
      }
      if (additionalPoints.has('B') && additionalPoints.has('E')) {
        additionalLines.push({ from: 'B', to: 'E', color: 'red', width: 1 });
      }
    }
    
    // Merge all results
    const allPoints = Array.from(new Set([...points, ...Array.from(additionalPoints)]));
    const allLines = [...lines, ...additionalLines];
    const allExtensions = [...extensions, ...additionalExtensions];
    
    console.log('Final geometry extraction:', {
      points: allPoints,
      lines: allLines,
      extensions: allExtensions,
      analysis: analysis
    });
    
    return { points: allPoints, lines: allLines, extensions: allExtensions };
  };

  // SVG Figure Data - State for each figure type
  const [figureData, setFigureData] = useState({
    triangle: {
      points: {
        A: { x: 200, y: 50, label: 'A' },
        B: { x: 100, y: 200, label: 'B' },
        C: { x: 300, y: 200, label: 'C' },
        D: { x: 150, y: 200, label: 'D' },
        E: { x: 250, y: 200, label: 'E' },
        M: { x: 200, y: 200, label: 'M' }
      },
      lines: [
        { from: 'A', to: 'B', color: 'gray', width: 1 },
        { from: 'B', to: 'C', color: 'gray', width: 1 },
        { from: 'C', to: 'A', color: 'gray', width: 1 },
        { from: 'A', to: 'D', color: 'red', width: 1.33 },
        { from: 'A', to: 'E', color: 'green', width: 1.33 },
        { from: 'A', to: 'M', color: 'blue', width: 1.33 }
      ]
    },
    circle: {
      points: {
        O: { x: 200, y: 200, label: 'O' },
        A: { x: 320, y: 200, label: 'A' },  // Right side of circle
        B: { x: 200, y: 80, label: 'B' },   // Top of circle
        C: { x: 80, y: 200, label: 'C' },   // Left side of circle
        D: { x: 200, y: 320, label: 'D' },  // Bottom of circle
        E: { x: 140, y: 140, label: 'E' },  // Top-left quadrant
        F: { x: 170, y: 260, label: 'F' }   // Intersection point inside circle
      },
      lines: [
        { from: 'A', to: 'C', color: 'red', width: 1.33 },    // Diameter AC
        { from: 'B', to: 'D', color: 'green', width: 1.33 },  // Chord BD
        { from: 'O', to: 'C', color: 'blue', width: 1 },   // Radius OC
        { from: 'B', to: 'D', color: 'purple', width: 1 }, // Chord BD
        { from: 'O', to: 'F', color: 'orange', width: 0.67 }, // Part of OC
        { from: 'F', to: 'C', color: 'orange', width: 0.67 }, // Part of OC
        { from: 'B', to: 'F', color: 'brown', width: 0.67 },  // Part of BD
        { from: 'F', to: 'D', color: 'brown', width: 0.67 }   // Part of BD
      ],
      circles: [
        { center: 'O', radius: 120, color: 'blue', width: 1 }
      ]
    },
    square: {
      points: {
        A: { x: 100, y: 100, label: 'A' },
        B: { x: 300, y: 100, label: 'B' },
        C: { x: 300, y: 300, label: 'C' },
        D: { x: 100, y: 300, label: 'D' },
        O: { x: 200, y: 200, label: 'O' }
      },
      lines: [
        { from: 'A', to: 'B', color: 'blue', width: 1 },
        { from: 'B', to: 'C', color: 'blue', width: 1 },
        { from: 'C', to: 'D', color: 'blue', width: 1 },
        { from: 'D', to: 'A', color: 'blue', width: 1 },
        { from: 'A', to: 'C', color: 'red', width: 1 },
        { from: 'B', to: 'D', color: 'green', width: 1 }
      ]
    },
    hexagon: {
      points: {
        A: { x: 200, y: 50, label: 'A' },
        B: { x: 350, y: 125, label: 'B' },
        C: { x: 350, y: 225, label: 'C' },
        D: { x: 200, y: 300, label: 'D' },
        E: { x: 50, y: 225, label: 'E' },
        F: { x: 50, y: 125, label: 'F' },
        O: { x: 200, y: 175, label: 'O' }
      },
      lines: [
        { from: 'A', to: 'B', color: 'gray', width: 1 },
        { from: 'B', to: 'C', color: 'gray', width: 1 },
        { from: 'C', to: 'D', color: 'gray', width: 1 },
        { from: 'D', to: 'E', color: 'gray', width: 1 },
        { from: 'E', to: 'F', color: 'gray', width: 1 },
        { from: 'F', to: 'A', color: 'gray', width: 1 },
        { from: 'A', to: 'O', color: 'red', width: 1 },
        { from: 'C', to: 'O', color: 'blue', width: 1 },
        { from: 'E', to: 'O', color: 'purple', width: 1 }
      ]
    }
  });

  // Default test content from test.txt
  const defaultTestContent = {
    question: "Question 13 (2 marks for part a, 5 marks for part b)",
    problem: "In Figure 1, $ABC$ is a triangle. $D$, $E$ and $M$ are all points on $BC$ such that $BD = CE$, $\\angle ADC = \\angle AEB$ and $DM = EM$.",
    figureDescription: "Triangle ABC with points D, E, M on side BC. Lines AD, AE, and AM are drawn from vertex A to these points on the base.",
    parts: [
      {
        id: 'a',
        question: "Prove that $\\triangle ACD \\cong \\triangle ABE$.",
        marks: 2
      },
      {
        id: 'b',
        question: "Suppose $AD = 15$ cm, $BD = 7$ cm and $DE = 18$ cm.",
        subParts: [
          {
            id: 'i',
            question: "Find $AM$.",
            marks: 3
          },
          {
            id: 'ii',
            question: "Is $\\triangle ABE$ a right-angled triangle? Explain your answer.",
            marks: 2
          }
        ],
        marks: 5
      }
    ]
  };

  // Mouse event handlers for dragging
  const handleMouseDown = (e, pointKey) => {
    setIsDragging(true);
    setDraggedPoint(pointKey);
  };

  const handleMouseMove = (e) => {
    if (isDragging && draggedPoint && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;
      
      setFigureData(prev => ({
        ...prev,
        [selectedFigure]: {
          ...prev[selectedFigure],
          points: {
            ...prev[selectedFigure].points,
            [draggedPoint]: {
              ...prev[selectedFigure].points[draggedPoint],
              x: Math.max(0, Math.min(400, x)),
              y: Math.max(0, Math.min(400, y))
            }
          }
        }
      }));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedPoint(null);
  };

  // Zoom and pan handlers
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.5, Math.min(3, prev * delta)));
  };

  const handleMouseDownPan = (e) => {
    if (!isDragging) {
      setIsDragging(true);
      setDraggedPoint('pan');
    }
  };

  const handleMouseMovePan = (e) => {
    if (isDragging && draggedPoint === 'pan') {
      setPan(prev => ({
        x: prev.x + e.movementX,
        y: prev.y + e.movementY
      }));
    }
  };

  // Enhanced pan with better boundaries
  const handlePan = (deltaX, deltaY) => {
    setPan(prev => ({
      x: Math.max(-200, Math.min(200, prev.x + deltaX)),
      y: Math.max(-200, Math.min(200, prev.y + deltaY))
    }));
  };

  // Keyboard controls for panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      const panStep = 20;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          handlePan(0, -panStep);
          break;
        case 'ArrowDown':
          e.preventDefault();
          handlePan(0, panStep);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePan(-panStep, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          handlePan(panStep, 0);
          break;
        case 'Home':
          e.preventDefault();
          setZoom(1);
          setPan({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset figure to original positions
  const resetFigure = () => {
    setFigureData({
      triangle: {
        points: {
          A: { x: 200, y: 50, label: 'A' },
          B: { x: 100, y: 200, label: 'B' },
          C: { x: 300, y: 200, label: 'C' },
          D: { x: 150, y: 200, label: 'D' },
          E: { x: 250, y: 200, label: 'E' },
          M: { x: 200, y: 200, label: 'M' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'gray', width: 1 },
          { from: 'B', to: 'C', color: 'gray', width: 1 },
          { from: 'C', to: 'A', color: 'gray', width: 1 },
          { from: 'A', to: 'D', color: 'red', width: 1.33 },
          { from: 'A', to: 'E', color: 'green', width: 1.33 },
          { from: 'A', to: 'M', color: 'blue', width: 1.33 }
        ]
      },
      circle: {
        points: {
          O: { x: 200, y: 200, label: 'O' },
          A: { x: 320, y: 200, label: 'A' },  // Right side of circle
          B: { x: 200, y: 80, label: 'B' },   // Top of circle
          C: { x: 80, y: 200, label: 'C' },   // Left side of circle
          D: { x: 200, y: 320, label: 'D' },  // Bottom of circle
          E: { x: 140, y: 140, label: 'E' },  // Top-left quadrant
          F: { x: 170, y: 260, label: 'F' }   // Intersection point inside circle
        },
        lines: [
          { from: 'A', to: 'C', color: 'red', width: 1.33 },    // Diameter AC
          { from: 'B', to: 'D', color: 'green', width: 1.33 },  // Chord BD
          { from: 'O', to: 'C', color: 'blue', width: 1 },   // Radius OC
          { from: 'B', to: 'D', color: 'purple', width: 1 }, // Chord BD
          { from: 'O', to: 'F', color: 'orange', width: 0.67 }, // Part of OC
          { from: 'F', to: 'C', color: 'orange', width: 0.67 }, // Part of OC
          { from: 'B', to: 'F', color: 'brown', width: 0.67 },  // Part of BD
          { from: 'F', to: 'D', color: 'brown', width: 0.67 }   // Part of BD
        ],
        circles: [
          { center: 'O', radius: 120, color: 'blue', width: 1 }
        ]
      },
      square: {
        points: {
          A: { x: 100, y: 100, label: 'A' },
          B: { x: 300, y: 100, label: 'B' },
          C: { x: 300, y: 300, label: 'C' },
          D: { x: 100, y: 300, label: 'D' },
          O: { x: 200, y: 200, label: 'O' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'blue', width: 1 },
          { from: 'B', to: 'C', color: 'blue', width: 1 },
          { from: 'C', to: 'D', color: 'blue', width: 1 },
          { from: 'D', to: 'A', color: 'blue', width: 1 },
          { from: 'A', to: 'C', color: 'red', width: 1 },
          { from: 'B', to: 'D', color: 'green', width: 1 }
        ]
      },
      hexagon: {
        points: {
          A: { x: 200, y: 50, label: 'A' },
          B: { x: 350, y: 125, label: 'B' },
          C: { x: 350, y: 225, label: 'C' },
          D: { x: 200, y: 300, label: 'D' },
          E: { x: 50, y: 225, label: 'E' },
          F: { x: 50, y: 125, label: 'F' },
          O: { x: 200, y: 175, label: 'O' }
        },
        lines: [
          { from: 'A', to: 'B', color: 'gray', width: 1 },
          { from: 'B', to: 'C', color: 'gray', width: 1 },
          { from: 'C', to: 'D', color: 'gray', width: 1 },
          { from: 'D', to: 'E', color: 'gray', width: 1 },
          { from: 'E', to: 'F', color: 'gray', width: 1 },
          { from: 'F', to: 'A', color: 'gray', width: 1 },
          { from: 'A', to: 'O', color: 'red', width: 1 },
          { from: 'C', to: 'O', color: 'blue', width: 1 },
          { from: 'E', to: 'O', color: 'purple', width: 1 }
        ]
      }
    });
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Download figure as SVG
  const downloadFigure = () => {
    if (svgRef.current) {
      const svgData = new XMLSerializer().serializeToString(svgRef.current);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = svgUrl;
      downloadLink.download = `${selectedFigure}-figure.svg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(svgUrl);
    }
  };

  // Function to safely render LaTeX content
  const renderLatexContent = (content) => {
    if (!content) return <span></span>;
    
    // Ensure the content is properly formatted for MathJax
    let formattedContent = content;
    
    // If the content contains LaTeX expressions but they're not properly wrapped
    // This is a simple heuristic - in a real app you might want more sophisticated parsing
    if (content.includes('\\') || content.includes('^') || content.includes('_')) {
      // Don't double-wrap if already wrapped
      if (!content.includes('$$') && !content.includes('$')) {
        // This is a simplified approach - in practice you'd want more sophisticated parsing
        formattedContent = content;
      }
    }
    
    return (
      <span 
        className="math-content"
        dangerouslySetInnerHTML={{ __html: formattedContent }}
        style={{
          fontFamily: 'inherit',
          lineHeight: '1.6'
        }}
      />
    );
  };

  // Get current figure data
  const currentFigure = figureData[selectedFigure] || {
    points: {},
    lines: [],
    circles: []
  };

  // Use processed content if available, otherwise use default content
  const testContent = processedContent || defaultTestContent;

  // Initialize MathJax when component mounts
  useEffect(() => {
    if (window.MathJax) {
      window.MathJax.typesetPromise();
    }
  }, [selectedFigure]);

  // Auto-update figure when processed content changes
  useEffect(() => {
    if (processedContent) {
      // Generate appropriate figure based on question content
      const figureType = generateFigureFromQuestion(processedContent);
      setSelectedFigure(figureType);
      
      // Create custom figure data if needed
      const customFigureData = createCustomFigureData(processedContent);
      setFigureData(prev => ({
        ...prev,
        [figureType]: customFigureData
      }));
    }
  }, [processedContent]);

  // Improved MathJax rendering
  useEffect(() => {
    const processMathJax = async () => {
      try {
        await ensureMathJaxReady();
        await renderMathJax(200);
      } catch (error) {
        console.log('MathJax rendering error:', error);
      }
    };

    // Render MathJax after a short delay to ensure DOM is ready
    const timer = setTimeout(processMathJax, 100);
    return () => clearTimeout(timer);
  }, [processedContent, testContent]);

  // Enhanced process question function with DeepSeek API
  const processQuestionInput = async () => {
    if (!questionInput.trim()) return;

    // Parse the question input first
    const parsedContent = parseQuestionInput(questionInput);
    setProcessedContent(parsedContent);

    // Extract figure description for DeepSeek analysis
    const figureDescription = parsedContent.figureDescription || '';
    const questionText = parsedContent.problem || '';

    // Check if there's a meaningful figure description
    const hasFigureDescription = figureDescription && 
      (figureDescription.toLowerCase().includes('figure') || 
       figureDescription.toLowerCase().includes('triangle') ||
       figureDescription.toLowerCase().includes('circle') ||
       figureDescription.toLowerCase().includes('square') ||
       figureDescription.toLowerCase().includes('points') ||
       figureDescription.toLowerCase().includes('lines'));

    if (hasFigureDescription) {
      // Only call DeepSeek API if there's a meaningful figure description
      setIsProcessing(true);
      setShowProgressModal(true);
      setProgressData({
        stage: 'connecting',
        message: '正在連接 DeepSeek API...',
        tokensUsed: 0,
        timeElapsed: 0,
        cost: 0
      });

      try {
        const result = await deepseekFigureParser.parseFigureDescription(
          figureDescription,
          questionText,
          (progress) => {
            setProgressData(progress);
          }
        );

        if (result.success) {
          // Update figure data with DeepSeek analysis
          const figureType = result.svgInstructions.figureType || 'triangle';
          setSelectedFigure(figureType);
          
          // Update figure data
          setFigureData(prev => ({
            ...prev,
            [figureType]: result.svgInstructions
          }));

          // Update progress to completed
          setProgressData(prev => ({
            ...prev,
            stage: 'completed',
            message: '圖形解析完成！',
            cost: result.cost
          }));
        } else {
          // Fallback to default parsing
          const figureType = generateFigureFromQuestion(parsedContent);
          setSelectedFigure(figureType);
          
          const customFigureData = createCustomFigureData(parsedContent);
          setFigureData(prev => ({
            ...prev,
            [figureType]: customFigureData
          }));

          setProgressData(prev => ({
            ...prev,
            stage: 'error',
            message: '圖形解析失敗，使用預設圖形',
            error: result.error
          }));
        }
      } catch (error) {
        console.error('Error processing question:', error);
        setProgressData(prev => ({
          ...prev,
          stage: 'error',
          message: '處理失敗',
          error: error.message
        }));
      } finally {
        setIsProcessing(false);
      }
    } else {
      // No meaningful figure description - clear the SVG viewer and skip DeepSeek API
      console.log('No figure description found, clearing SVG viewer');
      
      // Clear the figure data to show empty SVG viewer
      setFigureData(prev => ({
        ...prev,
        triangle: { points: {}, lines: [], circles: [] },
        circle: { points: {}, lines: [], circles: [] },
        rectangle: { points: {}, lines: [], circles: [] }
      }));
      
      // Set a default figure type but with empty data
      setSelectedFigure('triangle');
      
      // Show a brief message that no figure processing was needed
      setShowProgressModal(true);
      setProgressData({
        stage: 'completed',
        message: '問題處理完成（無圖形描述）',
        tokensUsed: 0,
        timeElapsed: 0,
        cost: 0
      });
      
      // Hide the modal after a short delay
      setTimeout(() => {
        setShowProgressModal(false);
      }, 1500);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <h1 className="text-4xl font-bold text-gray-800">Assessment</h1>
          {useKnowledgeGraph && (
            <div className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
              <Brain className="w-4 h-4" />
              <span>Knowledge Graph</span>
            </div>
          )}
        </div>
        <p className="text-xl text-gray-600">Mathematics Test with Interactive Geometry</p>
      </div>

      {/* Question Input Panel */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Question Input
          </h3>
          <button
            onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            {isLeftPanelCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>

        {!isLeftPanelCollapsed && (
          <>
            {/* Year Selection Dropdown (Knowledge Graph only) */}
            {useKnowledgeGraph && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Year
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 border border-purple-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <span className={selectedYear ? 'text-gray-900' : 'text-gray-500'}>
                      {selectedYear ? `HKDSE ${selectedYear}` : 'Select year...'}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-purple-400 transition-transform ${isYearDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Year Dropdown */}
                  {isYearDropdownOpen && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-purple-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="py-1">
                        {availableYears.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-gray-500">No years available</div>
                        ) : (
                          availableYears.map((year) => (
                            <button
                              key={year}
                              onClick={() => handleYearSelect(year)}
                              className="w-full px-3 py-2 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                            >
                              <div className="font-medium text-gray-900">HKDSE {year}</div>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Question Selection Dropdown */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Quick Question Selection
                </label>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setUseKnowledgeGraph(!useKnowledgeGraph)}
                    className={`flex items-center space-x-1 px-2 py-1 rounded text-xs transition-colors ${
                      useKnowledgeGraph 
                        ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                        : 'bg-gray-100 text-gray-600 border border-gray-300'
                    }`}
                  >
                    <Brain className="w-3 h-3" />
                    <span>Knowledge Graph</span>
                  </button>
                </div>
              </div>
              
              <div className="relative">
                <button
                  onClick={() => useKnowledgeGraph 
                    ? setIsKnowledgeGraphDropdownOpen(!isKnowledgeGraphDropdownOpen)
                    : setIsDropdownOpen(!isDropdownOpen)
                  }
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <span className={useKnowledgeGraph 
                    ? (selectedKnowledgeGraphQuestion ? 'text-gray-900' : 'text-gray-500')
                    : (selectedQuestion ? 'text-gray-900' : 'text-gray-500')
                  }>
                    {useKnowledgeGraph 
                      ? (selectedKnowledgeGraphQuestion ? selectedKnowledgeGraphQuestion.displayText : 'Select from Knowledge Graph...')
                      : (selectedQuestion ? selectedQuestion.displayText : 'Select a question from HKDSE papers...')
                    }
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${
                    useKnowledgeGraph ? (isKnowledgeGraphDropdownOpen ? 'rotate-180' : '') : (isDropdownOpen ? 'rotate-180' : '')
                  }`} />
                </button>

                {/* Knowledge Graph Dropdown */}
                {useKnowledgeGraph && isKnowledgeGraphDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-purple-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {/* Search input */}
                    <div className="p-2 border-b border-purple-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-400" />
                        <input
                          type="text"
                          placeholder="Search Knowledge Graph questions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-purple-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    {/* Question list */}
                    <div className="py-1">
                      {filteredKnowledgeGraphQuestions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No Knowledge Graph questions found</div>
                      ) : (
                        filteredKnowledgeGraphQuestions.map((question, index) => (
                          <button
                            key={`kg-${question.id}-${index}`}
                            onClick={() => handleKnowledgeGraphQuestionSelect(question)}
                            className="w-full px-3 py-2 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                          >
                            <div className="font-medium text-gray-900">
                              {selectedYear ? `${selectedYear} Q.${question.number}` : question.displayText}
                            </div>
                            <div className="text-xs text-purple-600 mb-1">{question.problemType} • {question.difficulty}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {question.problem.substring(0, 60)}...
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Regular HKDSE Dropdown */}
                {!useKnowledgeGraph && isDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search questions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Question list */}
                    <div className="py-1">
                      {isLoadingQuestions ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading questions...</div>
                      ) : filteredQuestions.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No questions found</div>
                      ) : (
                        filteredQuestions.map((question, index) => (
                          <button
                            key={`${question.year}-${question.number}-${index}`}
                            onClick={() => handleQuestionSelect(question)}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          >
                            <div className="font-medium text-gray-900">{question.displayText}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {question.problem.substring(0, 60)}...
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Question Input Textarea */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your mathematics question (supports LaTeX format)
              </label>
              <textarea
                value={questionInput}
                onChange={(e) => setQuestionInput(e.target.value)}
                placeholder="Enter your mathematics question here..."
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Process Question Button */}
            <button
              onClick={processQuestionInput}
              disabled={isProcessing || !questionInput.trim()}
              className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="w-4 h-4 mr-2" />
              {isProcessing ? 'Processing...' : 'Process Question'}
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Panel - Question Content */}
        <div className={`transition-all duration-300 ${isLeftPanelCollapsed ? 'lg:col-span-1' : 'lg:col-span-1'}`}>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Question Content</h2>
              <button
                onClick={() => setIsLeftPanelCollapsed(!isLeftPanelCollapsed)}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                {isLeftPanelCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
              </button>
            </div>

            {!isLeftPanelCollapsed && (
              <div className="space-y-6">
                {/* Question Header */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">{testContent.question}</h3>
                  <div className="text-gray-700 math-content">{renderLatexContent(testContent.problem)}</div>
                </div>

                {/* Figure Description */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="text-md font-semibold text-green-800 mb-2">Figure 1 Description:</h4>
                  <p className="text-gray-700">{testContent.figureDescription}</p>
                </div>

                {/* Question Parts */}
                <div className="space-y-4">
                  {testContent.parts.map((part) => (
                    <div key={part.id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-semibold text-gray-800">
                          Part ({part.id})
                        </h4>
                        <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {part.marks} marks
                        </span>
                      </div>
                      
                      <div className="text-gray-700 math-content mb-3">{renderLatexContent(part.question)}</div>
                      
                      {part.subParts && (
                        <div className="ml-4 space-y-3">
                          {part.subParts.map((subPart) => (
                            <div key={subPart.id} className="p-3 bg-white rounded border-l-4 border-blue-400">
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="text-md font-semibold text-gray-700">
                                  ({subPart.id})
                                </h5>
                                <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                                  {subPart.marks} marks
                                </span>
                              </div>
                              <div className="text-gray-700 math-content">{renderLatexContent(subPart.question)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - SVG Interactive Figure */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Interactive Figure</h2>
              <div className="flex items-center space-x-2">
                {/* Figure Selector */}
                <select
                  value={selectedFigure}
                  onChange={(e) => setSelectedFigure(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded text-sm"
                >
                  <option value="triangle">Triangle</option>
                  <option value="circle">Circle</option>
                  <option value="square">Square</option>
                  <option value="hexagon">Hexagon</option>
                </select>
                
                <button
                  onClick={resetFigure}
                  className="flex items-center space-x-1 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                  title="Reset Figure"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="text-sm">Reset</span>
                </button>
                
                <button
                  onClick={downloadFigure}
                  className="flex items-center space-x-1 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  title="Download Figure"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm">Download</span>
                </button>
              </div>
            </div>

            {/* SVG Interactive Figure Container */}
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
              {/* Zoom Controls */}
              <div className="absolute top-2 right-2 z-10 flex flex-col space-y-1">
                <button
                  onClick={() => setZoom(prev => Math.min(3, prev * 1.2))}
                  className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
                  title="Zoom In"
                >
                  <span className="text-gray-600 font-bold">+</span>
                </button>
                <button
                  onClick={() => setZoom(prev => Math.max(0.5, prev / 1.2))}
                  className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
                  title="Zoom Out"
                >
                  <span className="text-gray-600 font-bold">−</span>
                </button>
                <button
                  onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                  className="w-8 h-8 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 flex items-center justify-center"
                  title="Reset View"
                >
                  <span className="text-gray-600 text-xs">⌂</span>
                </button>
                {/* Zoom Level Indicator */}
                <div className="w-8 h-6 bg-white border border-gray-300 rounded shadow-sm flex items-center justify-center">
                  <span className="text-xs text-gray-600">{Math.round(zoom * 100)}%</span>
                </div>
              </div>
              
              <div 
                className="w-full h-96 bg-gray-50 relative cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDownPan}
                onMouseMove={handleMouseMovePan}
                onMouseUp={handleMouseUp}
                onWheel={handleWheel}
              >
                <svg
                  ref={svgRef}
                  width="100%"
                  height="100%"
                  viewBox="0 0 400 400"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: '0 0'
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseMove={(e) => e.stopPropagation()}
                  onMouseUp={(e) => e.stopPropagation()}
                >
                  {/* Grid */}
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e5e7eb" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid)" />

                  {/* Coordinate System Axes */}
                  {/* X-axis */}
                  <line 
                    x1="0" 
                    y1="200" 
                    x2="400" 
                    y2="200" 
                    stroke="#2563eb" 
                    strokeWidth="1.25" 
                    strokeLinecap="round"
                  />
                  {/* Y-axis */}
                  <line 
                    x1="200" 
                    y1="0" 
                    x2="200" 
                    y2="400" 
                    stroke="#dc2626" 
                    strokeWidth="1.25" 
                    strokeLinecap="round"
                  />
                  
                  {/* Axis Labels */}
                  <text x="390" y="195" fontSize="16" fontWeight="bold" fill="#2563eb">x</text>
                  <text x="205" y="15" fontSize="16" fontWeight="bold" fill="#dc2626">y</text>
                  <text x="185" y="215" fontSize="14" fontWeight="bold" fill="#374151">O</text>
                  
                  {/* Tick marks on X-axis */}
                  <line x1="100" y1="195" x2="100" y2="205" stroke="#2563eb" strokeWidth="0.75" />
                  <text x="100" y="220" fontSize="12" textAnchor="middle" fill="#2563eb">-2</text>
                  
                  <line x1="150" y1="195" x2="150" y2="205" stroke="#2563eb" strokeWidth="0.75" />
                  <text x="150" y="220" fontSize="12" textAnchor="middle" fill="#2563eb">-1</text>
                  
                  <line x1="250" y1="195" x2="250" y2="205" stroke="#2563eb" strokeWidth="0.75" />
                  <text x="250" y="220" fontSize="12" textAnchor="middle" fill="#2563eb">1</text>
                  
                  <line x1="300" y1="195" x2="300" y2="205" stroke="#2563eb" strokeWidth="0.75" />
                  <text x="300" y="220" fontSize="12" textAnchor="middle" fill="#2563eb">2</text>
                  
                  {/* Tick marks on Y-axis */}
                  <line x1="195" y1="100" x2="205" y2="100" stroke="#dc2626" strokeWidth="0.75" />
                  <text x="180" y="105" fontSize="12" textAnchor="end" fill="#dc2626">2</text>
                  
                  <line x1="195" y1="150" x2="205" y2="150" stroke="#dc2626" strokeWidth="0.75" />
                  <text x="180" y="155" fontSize="12" textAnchor="end" fill="#dc2626">1</text>
                  
                  <line x1="195" y1="250" x2="205" y2="250" stroke="#dc2626" strokeWidth="0.75" />
                  <text x="180" y="255" fontSize="12" textAnchor="end" fill="#dc2626">-1</text>
                  
                  <line x1="195" y1="300" x2="205" y2="300" stroke="#dc2626" strokeWidth="0.75" />
                  <text x="180" y="305" fontSize="12" textAnchor="end" fill="#dc2626">-2</text>

                  {/* Draw circles for circle figure */}
                  {currentFigure && currentFigure.circles && currentFigure.circles.map((circle, index) => {
                    const center = currentFigure.points && currentFigure.points[circle.center];
                    if (center && typeof center.x === 'number' && typeof center.y === 'number') {
                      return (
                        <circle
                          key={`circle-${index}`}
                          cx={center.x}
                          cy={center.y}
                          r={circle.radius}
                          fill="none"
                          stroke={circle.color}
                          strokeWidth={circle.width}
                        />
                      );
                    }
                    return null;
                  })}

                  {/* Draw lines */}
                  {currentFigure && currentFigure.lines && currentFigure.lines.map((line, index) => {
                    const from = currentFigure.points && currentFigure.points[line.from];
                    const to = currentFigure.points && currentFigure.points[line.to];
                    if (from && to) {
                      return (
                        <line
                          key={`line-${index}`}
                          x1={from.x}
                          y1={from.y}
                          x2={to.x}
                          y2={to.y}
                          stroke={line.color}
                          strokeWidth={line.width}
                          strokeLinecap="round"
                          strokeDasharray={line.strokeDasharray || "none"}
                        />
                      );
                    }
                    return null;
                  })}

                  {/* Draw extensions (dashed lines) */}
                  {currentFigure && currentFigure.extensions && currentFigure.extensions.map((extension, index) => {
                    const from = currentFigure.points && currentFigure.points[extension.from];
                    const to = currentFigure.points && currentFigure.points[extension.to];
                    if (from && to) {
                      return (
                        <line
                          key={`extension-${index}`}
                          x1={from.x}
                          y1={from.y}
                          x2={to.x}
                          y2={to.y}
                          stroke={extension.color || "gray"}
                          strokeWidth={extension.width || 0.67}
                          strokeLinecap="round"
                          strokeDasharray={extension.strokeDasharray || "5,5"}
                        />
                      );
                    }
                    return null;
                  })}

                  {/* Draw points */}
                  {currentFigure && currentFigure.points && Object.entries(currentFigure.points).map(([key, point]) => {
                    if (point && typeof point.x === 'number' && typeof point.y === 'number') {
                      return (
                        <g key={key}>
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r="6"
                            fill="white"
                            stroke="black"
                            strokeWidth="2"
                            style={{ cursor: 'pointer' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                            onMouseMove={(e) => handleMouseMove(e)}
                            onMouseUp={handleMouseUp}
                          />
                          <text
                            x={point.x + 12}
                            y={point.y + 4}
                            fontSize="14"
                            fontWeight="bold"
                            fill="black"
                          >
                            {point.label}
                          </text>
                        </g>
                      );
                    }
                    return null;
                  })}
                </svg>
              </div>
            </div>

            {/* Figure Controls */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-md font-semibold text-gray-800 mb-2">Figure Controls</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {/* Dynamic controls based on current figure */}
                {currentFigure && currentFigure.lines && currentFigure.lines.map((line, index) => (
                  <div key={`line-${index}`} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: line.color }}
                    ></div>
                    <span>Line {line.from}{line.to}</span>
                  </div>
                ))}
                {currentFigure && currentFigure.circles && currentFigure.circles.map((circle, index) => (
                  <div key={`circle-${index}`} className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span>Circle {circle.center}</span>
                  </div>
                ))}
                {/* Show points if no lines are available */}
                {currentFigure && currentFigure.points && Object.keys(currentFigure.points).length > 0 && 
                 (!currentFigure.lines || currentFigure.lines.length === 0) && (
                  <div className="col-span-2">
                    <p className="text-xs text-gray-600 mb-2">Points:</p>
                    <div className="grid grid-cols-3 gap-1">
                      {Object.entries(currentFigure.points).map(([key, point]) => (
                        <div key={key} className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-black rounded-full"></div>
                          <span className="text-xs">{point.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs text-gray-600">
                <p>💡 <strong>Interactive Features:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Drag points to explore the geometry</li>
                  <li>Drag background to pan the figure</li>
                  <li>Scroll to zoom in/out</li>
                  <li>Use zoom controls (+/-) or keyboard arrows to navigate</li>
                  <li>Press 'Home' key to reset view</li>
                  <li>Click Reset to restore original positions</li>
                  <li>Use dropdown to switch between different figures</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Assessment Instructions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
            <Clock className="w-5 h-5 text-blue-500" />
            <div>
              <p className="font-semibold text-blue-800">Time Limit</p>
              <p className="text-sm text-blue-600">15 minutes</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
            <Target className="w-5 h-5 text-green-500" />
            <div>
              <p className="font-semibold text-green-800">Total Marks</p>
              <p className="text-sm text-green-600">7 marks</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
            <BookOpen className="w-5 h-5 text-purple-500" />
            <div>
              <p className="font-semibold text-purple-800">Topic</p>
              <p className="text-sm text-purple-600">Triangle Congruence</p>
            </div>
          </div>
        </div>
      </div>

      {/* DeepSeek Progress Modal */}
      <DeepSeekProgressModal
        isOpen={showProgressModal}
        progress={progressData}
        onClose={() => setShowProgressModal(false)}
      />
    </div>
  );
};

export default Assessment; 